import React from 'react';
import { Box, MenuItem, Select } from '@mui/material';
import { useLanguage, LANGUES as LANGUES_SERVIES } from '../contexts/LanguageContext';

// Composant pour le drapeau français
const FrenchFlag = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
    <rect width="8" height="18" fill="#002395"/>
    <rect x="8" width="8" height="18" fill="#FFFFFF"/>
    <rect x="16" width="8" height="18" fill="#ED2939"/>
  </svg>
);

// Composant pour le drapeau anglais
const EnglishFlag = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
    <rect width="24" height="18" fill="#012169"/>
    <path d="M0 0L24 18M24 0L0 18" stroke="#FFFFFF" strokeWidth="2"/>
    <path d="M12 0V18M0 9H24" stroke="#FFFFFF" strokeWidth="2"/>
    <path d="M0 0L24 18M24 0L0 18" stroke="#C8102E" strokeWidth="1.2"/>
    <path d="M12 0V18M0 9H24" stroke="#C8102E" strokeWidth="1.2"/>
  </svg>
);

// Composant pour le drapeau israélien
const IsraeliFlag = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
    {/* Fond blanc */}
    <rect width="24" height="18" fill="#FFFFFF"/>

    {/* Bande bleue supérieure */}
    <rect y="2" width="24" height="1.5" fill="#0038B8"/>

    {/* Bande bleue inférieure */}
    <rect y="14.5" width="24" height="1.5" fill="#0038B8"/>

    {/* Étoile de David (Magen David) - composée de deux triangles */}
    <g transform="translate(12, 9)">
      {/* Triangle pointant vers le haut */}
      <path d="M 0,-3.5 L 3,1.5 L -3,1.5 Z" fill="none" stroke="#0038B8" strokeWidth="0.6"/>

      {/* Triangle pointant vers le bas */}
      <path d="M 0,3.5 L 3,-1.5 L -3,-1.5 Z" fill="none" stroke="#0038B8" strokeWidth="0.6"/>
    </g>
  </svg>
);

/**
 * ⚠️ CHAQUE LANGUE EST NOMMÉE DANS SA PROPRE LANGUE, ET CE N'EST PAS UNE
 * NÉGLIGENCE DE TRADUCTION. « עברית » ne devient pas « Hébreu » quand la page
 * est en français : celui qui cherche sa langue ne lit pas encore celle qui est
 * affichée. *Traduire le nom d'une langue le rend illisible à la seule personne
 * qui en a besoin.* Ces trois chaînes n'ont donc rien à faire dans les locales.
 */
/**
 * ⚠️ LES CODES VIENNENT DE `LanguageContext`, PAS D'UNE QUATRIÈME LISTE. Ce
 * fichier énumérait `fr`/`en`/`he` de son côté pendant que le contexte les
 * énumérait trois fois du sien. **Ajouter l'arabe aurait demandé quatre
 * modifications, dont trois invisibles depuis la quatrième** — et la première
 * oubliée aurait donné une langue choisissable que le contexte refuse, ou
 * l'inverse. *Le nom et le drapeau appartiennent à l'écran ; la LISTE
 * appartient au contexte.*
 */
const HABILLAGE = {
  fr: { nom: 'Français', Drapeau: FrenchFlag },
  en: { nom: 'English', Drapeau: EnglishFlag },
  he: { nom: 'עברית', Drapeau: IsraeliFlag },
};

const LANGUES = Object.keys(LANGUES_SERVIES).map((code) => ({
  code, ...HABILLAGE[code],
}));

/**
 * Le choix de langue — une LISTE, plus un bouton qui fait défiler.
 *
 * 🔴 CE QU'IL ÉTAIT, ET POURQUOI C'ÉTAIT UN CUL-DE-SAC. Un `IconButton` qui
 * cyclait `fr → en → he → fr`, portant le drapeau COURANT et rien d'autre.
 * Deux défauts, et le second est le vrai :
 *   · **il n'annonçait pas ses options.** Rien à l'écran ne disait que l'hébreu
 *     existait ; il fallait cliquer deux fois pour le découvrir, et un
 *     troisième clic pour revenir.
 *   · **il montrait l'état, pas l'action.** Un drapeau français sur un bouton
 *     se lit « cette page est en français » aussi bien que « cliquez pour le
 *     français ». L'infobulle levait l'ambiguïté — pour qui la survole.
 *
 * *Un contrôle qui cache ses options fait deviner ; une liste fait choisir.*
 *
 * ⚠️ ET IL SERT CINQ SURFACES, dont deux hors session — le Login et le tunnel
 * d'invitation. C'est là que le défaut coûtait le plus : un invité hébréophone
 * n'est jamais entré dans l'application, sa langue vaut le défaut `'fr'` câblé
 * dans `LanguageContext`, et la liste est son seul recours.
 *
 * ⚠️ `color: 'inherit'` PARTOUT — barre sombre du Header, ardoise de la console
 * super-admin, fond clair des deux portes. La liste déroulante elle-même est un
 * `Paper` : elle suit le mode sans qu'on ait à le lui dire.
 */
const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();

  const rendre = (code) => {
    const { nom, Drapeau } = LANGUES.find((l) => l.code === code) || LANGUES[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Drapeau />
        <span>{nom}</span>
      </Box>
    );
  };

  return (
    <Select
      value={language}
      onChange={(ev) => changeLanguage(ev.target.value)}
      variant="standard"
      disableUnderline
      inputProps={{ 'aria-label': 'Langue · Language · שפה' }}
      renderValue={rendre}
      sx={{
        color: 'inherit',
        fontSize: '0.875rem',
        '& .MuiSelect-icon': { color: 'inherit' },
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5 },
      }}
    >
      {LANGUES.map(({ code }) => (
        <MenuItem key={code} value={code}>{rendre(code)}</MenuItem>
      ))}
    </Select>
  );
};

export default LanguageSwitcher;
